import { IInlineSearchActions } from '@/interfaces/actions.interface';
import store from '@/store';
import TabsKind from '@/enums/tabs.enum';
import globals from '@/utils/global';
import { InlineSearchAppClearInput } from '@studyportals/inlinesearch-interfaces';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { disciplinesAll } from '@studyportals/static-domain-data/disciplines/disciplines';
import { FacetValues, ProgrammeFilterType, ProgrammeSearchInput } from '@studyportals/search-api-interface';
import { countryGetIsoCode  } from '@studyportals/static-domain-data/countries/country-get-iso-code';
import { SuggestionResult } from '@/interfaces/suggestions-result.interface';
import { EventAggregationService } from "@/utils/EventAggregationService";
import { SearchApiRedirectionService } from '@studyportals/omnisearch-interfaces';
import PathUtils from '@/utils/PathUtils';


const actions: IInlineSearchActions = {
	updateProgrammesResults: async (text) => {
		if (!store.getters.amIVisible()) return;

		store.mutations.setSelectedTab(TabsKind.SEARCHING_FOR);

		const hasProgrammeSearchTextValue = store.getters.getProgrammeSearchInput()?.text;
		const hasAnyText = store.getters.getText();

		if (text) {
			store.mutations.setText(text);
		}

		let withLocalTextSearchInput = {} as ProgrammeSearchInput;

		if (!hasAnyText && hasProgrammeSearchTextValue) {
			withLocalTextSearchInput = {
				text: store.getters.getText(),
				...store.getters.getProgrammeSearchInput()!,
			};
		} else {
			withLocalTextSearchInput = {
				...store.getters.getProgrammeSearchInput()!,
				text: store.getters.getText()
			};
		}

		withLocalTextSearchInput.nationalityCountryIso = countryGetIsoCode(store.getters.getUserNationality().toString())!;

		store.mutations.setText(withLocalTextSearchInput.text!);

		if (!withLocalTextSearchInput.text
			|| withLocalTextSearchInput.text.length < globals.MIN_SEARCH_LENGTH) {
			resetResults(TabsKind.SEARCHING_FOR);
			return;
		}

		const results = await globals.resultsRepository.getResult(withLocalTextSearchInput);

		const anyResults = results && results.hitsTotal > 0;
		if (!anyResults) {
			resetResults(TabsKind.PROGRAMMES);
			return;
		}

		store.mutations.setResults(TabsKind.PROGRAMMES, results);
		store.mutations.setTabResultsAmount(TabsKind.PROGRAMMES, results.hitsTotal);
		store.mutations.setSelectedTab(TabsKind.PROGRAMMES);
	},
	loadDisciplineFacets: async () => {
		const facets = await globals.disciplineFacetsRepository.getDisciplineFacets();

		if (!facets.length) return;

		const disciplinesIds: string[] = disciplinesAll.map(d => d.id);
		const sortedFacets: FacetValues[] = facets.sort((a, b) => disciplinesIds.indexOf(a.value) - disciplinesIds.indexOf(b.value)
		);

		store.mutations.setFacets({ [FilterType.DISCIPLINES]: sortedFacets });
	},
	setDisciplineAsFilter: async (discipline: FacetValues): Promise<void> => {
		store.actions.clearText();

		await globals.omniSearchApplication.setFilters({
			[FilterType.KEYWORD]: [],
			[FilterType.DISCIPLINES]: [discipline.value],
		} as unknown as Record<FilterType, string[]>);
	},
	updateSuggestions: async (text: string): Promise<void> => {
		const hasProgrammeSearchTextValue = store.getters.getProgrammeSearchInput()?.text;
		const hasAnyText = store.getters.getText();

		if (!text) {
			store.mutations.setSuggestions([]);
			store.mutations.setText('');
			return;
		}

		store.mutations.setText(text);

		let withLocalTextSearchInput = {} as ProgrammeSearchInput;

		if (!hasAnyText && hasProgrammeSearchTextValue) {
			withLocalTextSearchInput = {
				text,
				...store.getters.getProgrammeSearchInput()!,
			};
		} else {
			withLocalTextSearchInput = {
				...store.getters.getProgrammeSearchInput()!,
				text: store.getters.getText(),
			};
		}

		const suggestions = await globals.suggestionsRepository.getSuggestions(
			withLocalTextSearchInput
		);

		if (!suggestions || !suggestions.length) {
			store.mutations.setSuggestions([]);
			return;
		}

		const mappedSuggestions =  suggestions?.map((suggestion: SuggestionResult) => {
			return ({
				originalText: text.toLowerCase().trim(),
				fullText: suggestion.suggestion.toLowerCase().trim(),
				programmesCount: suggestion.amount_of_programmes,
				filterSets: suggestion.filterSets,
			})
		});

		if(mappedSuggestions){
			store.mutations.setSuggestions(mappedSuggestions);
			return;
		}

		store.mutations.setSuggestions([{
			originalText: text.toLowerCase().trim(),
			fullText: `<match>${text.toLowerCase().trim()}</match>`,
			programmesCount: 0,
			filterSets: [],
		}]);
	},
	updateSearchText: (): Promise<void> => {
		return globals.omniSearchApplication.setFilters({
			[FilterType.KEYWORD]: [store.getters.getText() || '']
		} as Record<FilterType, string[]>);
	},
	updateProgrammesFromSuggestions: async (text?: string): Promise<void> => {
		if (!text) return;

		const suggestions =  store.getters.getSuggestions();

		if (!suggestions || !suggestions.length) {
			store.mutations.setSuggestions([]);
			return;
		}

		const suggestion = suggestions.find(x =>
			x.fullText
				.replace(/<match>|<\/match>/g, '')
				.includes(text.trim())
		);

		if(!suggestion ||  !suggestion?.filterSets || !suggestion.filterSets.length){
			await redirectOrSetFilters(
				[{[ProgrammeFilterType.TEXT]: text.trim()}] as Record<ProgrammeFilterType, string>[]
			);
			return;
		}

		const mappedFilterSets = suggestion.filterSets
			.map((filterSet) => {
				const filters = filterSet.filters.map((filter) => ({
					[filter.type]: filter.value,
				}));
				return filters;
			})
			.flat() as Record<ProgrammeFilterType, string>[];

		await redirectOrSetFilters(mappedFilterSets);
	},
	clearText: (): void =>  {
		store.mutations.setText('');
		EventAggregationService.instance?.publishTo(InlineSearchAppClearInput.EventType, new InlineSearchAppClearInput());
	}
};

const redirectOrSetFilters = async (mappedFilterSets: Record<ProgrammeFilterType, string>[]): Promise<void> => {
	const isRedirected = SearchApiRedirectionService.handleRedirectionFromSuggestion(
		mappedFilterSets,
		PathUtils.portalPathFragment(),
		"suggestion_click"
	);

	if(isRedirected) return;

	const filtersToSet = SearchApiRedirectionService.transformFiltersFromSearchApi(mappedFilterSets);
	await globals.omniSearchApplication.setFilters(filtersToSet);
};

const resetResults = (tab: TabsKind): void => {
	store.mutations.setResults(tab, null);
	store.mutations.setTabResultsAmount(tab, 0);
}

export default actions;